/**
 * Why a clue forces the mark a hint names.
 *
 * The hint engine can tell a reader which clue to look at and which square it
 * lands on. That is a nudge, and a nudge is what most readers want. But when
 * they cannot see it, being told the answer a second time is no help at all:
 * what they are missing is the method, and the method is always the same one.
 * Take the spots each side of the clue can still occupy. Try the one in
 * question. Find that nothing on the other side is left to pair with it.
 *
 * So this walks that check out loud. It is the same support test that
 * `applyRelations` and `applyBetweens` run, read back in words, rather than a
 * second and prettier account that might not match what the solver did.
 *
 * It only speaks for a cell the clue settles on its own, in one reading. That
 * is not a limitation so much as the point — `nextHint` now prefers such a cell
 * precisely so there is something to say here, because a clue credited with a
 * conclusion four propagation steps downstream cannot be justified in a
 * paragraph and should not be dressed up as if it can.
 */
import type { Clue, Puzzle } from "@gen/logic-grid/types.ts";
import { isBetween, relationPredicate } from "@gen/logic-grid/solver-propagate.ts";
import { applyClueOnce, gridFromMarks } from "./hint.ts";
import type { Cell, Marks } from "./marks.ts";

/** One end of a clue: a value in a category, which the grid places somewhere. */
interface Anchor {
  cat: number;
  val: number;
}

/** "Cook Omar", "Spot 3rd" — the way every other line of hint copy names one. */
function name(puzzle: Puzzle, cat: number, val: number): string {
  const c = puzzle.categories[cat];
  return `${c?.name} ${c?.values[val]}`;
}

/** "1st", or "2nd, 3rd or 4th" — spots, where the category is already obvious. */
function spots(puzzle: Puzzle, orderCat: number, vals: number[]): string {
  const parts = vals.map((v) => puzzle.categories[orderCat]?.values[v] ?? "?");
  if (parts.length === 0) return "nowhere";
  if (parts.length === 1) return parts[0]!;
  return `${parts.slice(0, -1).join(", ")} or ${parts[parts.length - 1]}`;
}

/**
 * A cell read as "this end of the clue, at this spot".
 *
 * Every positional elimination has that shape: one side of the cell is the
 * order category, the other is something the clue actually mentions. Null when
 * the cell is neither, which is how the non-positional consequences of a clue
 * get routed elsewhere.
 */
function asPlacement(
  cell: Cell,
  orderCat: number,
  anchors: Anchor[],
): { at: number; spot: number } | null {
  const onOrder =
    cell.rowCat === orderCat ? cell.rowVal : cell.colCat === orderCat ? cell.colVal : null;
  if (onOrder === null) return null;
  const other =
    cell.rowCat === orderCat
      ? { cat: cell.colCat, val: cell.colVal }
      : { cat: cell.rowCat, val: cell.rowVal };
  const at = anchors.findIndex((a) => a.cat === other.cat && a.val === other.val);
  return at === -1 ? null : { at, spot: onOrder };
}

/** Whether a cell is exactly the pair between two named ends of a clue. */
function isPairOf(cell: Cell, one: Anchor, two: Anchor): boolean {
  const matches = (a: Anchor, b: Anchor) =>
    cell.rowCat === a.cat &&
    cell.rowVal === a.val &&
    cell.colCat === b.cat &&
    cell.colVal === b.val;
  return matches(one, two) || matches(two, one);
}

/**
 * Why one end of a clue cannot stand at one spot.
 *
 * Returns null when it can — which is not a case the callers below should ever
 * reach, but returning a sentence that says otherwise would be worse than
 * saying nothing.
 */
function whyNotThere(
  puzzle: Puzzle,
  marks: Marks,
  clue: Clue,
  anchors: Anchor[],
  at: number,
  spot: number,
): string | null {
  const orderCat = clue.orderCat;
  if (orderCat === undefined) return null;
  const grid = gridFromMarks(puzzle, marks);
  const me = name(puzzle, anchors[at]!.cat, anchors[at]!.val);
  const here = puzzle.categories[orderCat]?.values[spot];

  if (anchors.length === 2) {
    const ok = relationPredicate(clue.kind, clue.distance ?? 1);
    const them = anchors[1 - at]!;
    const open = grid.candidates(them.cat, orderCat, them.val);
    const needed: number[] = [];
    for (let q = 0; q < puzzle.size; q++) {
      if (at === 0 ? ok(spot, q) : ok(q, spot)) needed.push(q);
    }
    if (needed.some((q) => open.includes(q))) return null;
    const theirName = name(puzzle, them.cat, them.val);
    if (needed.length === 0) {
      // The requirement left the row entirely. Worth naming, because a reader
      // hunting for the clash will not find one: there is nothing to clash with.
      return clue.kind === "before"
        ? `${me} at ${here} would need ${theirName} on a side of it that has no spots at all`
        : `${me} at ${here} would put ${theirName} off the end of the row, since counting out from ${here} lands outside it`;
    }
    return `${me} at ${here} would put ${theirName} at ${spots(puzzle, orderCat, needed)}, and your grid has ${theirName} down to ${spots(puzzle, orderCat, open)}`;
  }

  // Three ends: the middle one and the two it sits between. Fixing one still
  // leaves a choice, so the argument has to try each spot the second could take
  // and follow it through to the third.
  const others = [0, 1, 2].filter((i) => i !== at);
  const [firstAt, secondAt] = others as [number, number];
  const first = anchors[firstAt]!;
  const second = anchors[secondAt]!;
  const firstOpen = grid.candidates(first.cat, orderCat, first.val);
  const secondOpen = grid.candidates(second.cat, orderCat, second.val);

  /*
   * The middle one at either end of the row. This is the case worth calling out
   * by name rather than grinding through: there is nothing on one side of an end
   * spot, so nothing standing there can have something on both sides of it.
   */
  if (at === 0 && (spot === 0 || spot === puzzle.size - 1)) {
    return `${me} at ${here} has nothing on one side of it, and the clue needs one of the other two on each side. An end spot can never be the middle of anything`;
  }

  const branches: string[] = [];
  for (const f of firstOpen) {
    const needed: number[] = [];
    for (let s = 0; s < puzzle.size; s++) {
      const trio = [0, 0, 0];
      trio[at] = spot;
      trio[firstAt] = f;
      trio[secondAt] = s;
      if (isBetween(trio[0]!, trio[1]!, trio[2]!)) needed.push(s);
    }
    if (needed.some((s) => secondOpen.includes(s))) return null;
    const firstHere = puzzle.categories[orderCat]?.values[f];
    branches.push(
      needed.length === 0
        ? `${name(puzzle, first.cat, first.val)} at ${firstHere} leaves ${name(puzzle, second.cat, second.val)} nowhere to go`
        : `${name(puzzle, first.cat, first.val)} at ${firstHere} would need ${name(puzzle, second.cat, second.val)} at ${spots(puzzle, orderCat, needed)}, which is not open`,
    );
  }
  if (branches.length === 0) return null;
  // Every branch failing the same way is one fact, not four. Spelling it out
  // four times reads as padding and buries the reason it fails.
  const allBlank = branches.every((b) => b.endsWith("nowhere to go"));
  if (allBlank && branches.length > 1) {
    return `${me} at ${here} cannot be made to work: wherever ${name(puzzle, first.cat, first.val)} goes, there is no spot left for ${name(puzzle, second.cat, second.val)} on the far side`;
  }
  return `${me} at ${here} cannot be made to work: ${branches.join("; ")}`;
}

/** The stock of spots each end of the clue still has, which is where to start. */
function stateOfPlay(puzzle: Puzzle, marks: Marks, clue: Clue, anchors: Anchor[]): string {
  const orderCat = clue.orderCat!;
  const grid = gridFromMarks(puzzle, marks);
  const parts = anchors.map((a) => {
    const open = grid.candidates(a.cat, orderCat, a.val);
    return `${name(puzzle, a.cat, a.val)} at ${spots(puzzle, orderCat, open)}`;
  });
  const joined =
    parts.length > 1
      ? `${parts.slice(0, -1).join("; ")}; and ${parts[parts.length - 1]}`
      : parts[0];
  return `Start from what your grid already allows: ${joined}.`;
}

/** A clue measured along an order category: the ones readers get stuck on. */
function positionalWhy(
  puzzle: Puzzle,
  marks: Marks,
  clue: Clue,
  cell: Cell,
  mark: "tick" | "cross",
): string | null {
  const orderCat = clue.orderCat;
  if (orderCat === undefined) return null;
  const anchors: Anchor[] = [clue.a, clue.b, ...(clue.c ? [clue.c] : [])].map((r) => ({
    cat: r.cat,
    val: r.val,
  }));

  // The quiet half of these clues: they also say their ends are different ones.
  if (mark === "cross") {
    for (let i = 0; i < anchors.length; i++) {
      for (let j = i + 1; j < anchors.length; j++) {
        if (!isPairOf(cell, anchors[i]!, anchors[j]!)) continue;
        const impossible =
          clue.kind === "before"
            ? "nothing comes before itself"
            : clue.kind === "between"
              ? "nothing sits between itself and something else"
              : "nothing stands a given number of spots away from itself";
        return `This one needs no counting at all. The clue speaks of ${name(puzzle, anchors[i]!.cat, anchors[i]!.val)} and ${name(puzzle, anchors[j]!.cat, anchors[j]!.val)} as two separate ones, and ${impossible}, so they cannot turn out to be the same. Every clue of this shape gives you that cross for free, before you count anything.`;
      }
    }
  }

  if (mark === "cross") {
    const placed = asPlacement(cell, orderCat, anchors);
    if (!placed) return null;
    const why = whyNotThere(puzzle, marks, clue, anchors, placed.at, placed.spot);
    if (!why) return null;
    return `${stateOfPlay(puzzle, marks, clue, anchors)} Now test it: ${why}. So that square is out.`;
  }
  return lineLeftOver(puzzle, marks, clue, anchors, cell);
}

/**
 * A tick, explained as the last one standing.
 *
 * Almost no clue says "this one goes here". What they do is take a spot away,
 * and a line that was down to two becomes a line that is down to one — often in
 * a block the clue does not even mention, which is why these are the ticks
 * readers find least obvious. So the account has to name three things: what the
 * line held, what the clue removed from it, and why that removal holds.
 */
function lineLeftOver(
  puzzle: Puzzle,
  marks: Marks,
  clue: Clue,
  anchors: Anchor[],
  cell: Cell,
): string | null {
  // A clue with no order category still completes lines — it just cannot show
  // its working, because there is no counting to walk through.
  const orderCat = clue.orderCat;
  const base = gridFromMarks(puzzle, marks);
  const once = applyClueOnce(puzzle, marks, clue);
  if (!once) return null;

  /* The two lines through the cell: along its block's row, and down its column. */
  const lines: Array<{ fixed: number; fixedVal: number; along: number; at: (v: number) => Cell }> =
    [
      {
        fixed: cell.rowCat,
        fixedVal: cell.rowVal,
        along: cell.colCat,
        at: (v) => ({ ...cell, colVal: v }),
      },
      {
        fixed: cell.colCat,
        fixedVal: cell.colVal,
        along: cell.rowCat,
        at: (v) => ({ ...cell, rowVal: v }),
      },
    ];

  for (const line of lines) {
    const after = once.candidates(line.fixed, line.along, line.fixedVal);
    if (after.length !== 1) continue;
    const before = base.candidates(line.fixed, line.along, line.fixedVal);
    const removed = before.filter((v) => !after.includes(v));
    if (removed.length === 0) continue;

    const held = before.map((v) => name(puzzle, line.along, v));
    const stock = `Your grid had ${name(puzzle, line.fixed, line.fixedVal)} down to ${held.slice(0, -1).join(", ")} or ${held[held.length - 1]}.`;
    const gone = removed.map((v) => name(puzzle, line.along, v));
    const knocked = `The clue knocks out ${gone.slice(0, -1).join(", ")}${gone.length > 1 ? " and " : ""}${gone[gone.length - 1]}`;

    const placed =
      orderCat === undefined ? null : asPlacement(line.at(removed[0]!), orderCat, anchors);
    const worked = placed
      ? whyNotThere(puzzle, marks, clue, anchors, placed.at, placed.spot)
      : null;
    const because = worked ? `: ${worked}` : "";
    const left = name(puzzle, line.along, after[0]!);
    return `${stock} ${knocked}${because}. That leaves ${left}, so it has to be the one.`;
  }
  return null;
}

/** The clue kinds that say what they mean with no arithmetic in the way. */
function plainWhy(
  puzzle: Puzzle,
  marks: Marks,
  clue: Clue,
  cell: Cell,
  mark: "tick" | "cross",
): string | null {
  if (clue.kind === "not") {
    // A flat denial can still produce a tick, by taking the last rival out of a
    // line. Saying "cross it and move on" at a square that wants a tick would
    // be the sort of wrongness that costs a reader their afternoon.
    if (mark === "tick") {
      const anchors: Anchor[] = [clue.a, clue.b].map((r) => ({ cat: r.cat, val: r.val }));
      return lineLeftOver(puzzle, marks, clue, anchors, cell);
    }
    return "The clue rules this pair out in so many words. There is nothing to work out here, which is worth knowing in itself: cross it and move on.";
  }
  if (clue.kind === "same") {
    const a = name(puzzle, clue.a.cat, clue.a.val);
    const b = name(puzzle, clue.b.cat, clue.b.val);
    return mark === "tick"
      ? `The clue says ${a} and ${b} are one and the same, and this is the square where those two meet.`
      : `The clue ties ${a} to ${b}. Once two things are the same one, every other square in both of their lines is out, and this is one of those.`;
  }
  if (clue.kind === "either" && clue.c) {
    const a = name(puzzle, clue.a.cat, clue.a.val);
    const one = name(puzzle, clue.b.cat, clue.b.val);
    const two = name(puzzle, clue.c.cat, clue.c.val);
    return mark === "tick"
      ? `The clue leaves ${a} only ${one} and ${two} to choose from, and your grid has already crossed one of them out. When a choice of two loses one, the other is settled.`
      : `The clue says ${a} is ${one} or ${two}, and this square is neither. A clue like that is worth more than the single cross it gets quoted for: it shuts that whole line down to two squares at once.`;
  }
  return null;
}

/**
 * The reasoning behind a clue hint, or null when there is none worth printing.
 *
 * Null rather than waffle. A conditional or a counting clue moves the grid by a
 * route that does not shorten into a sentence, and a vague paragraph gesturing
 * at one would be the same unhelpful move as repeating the answer louder.
 */
export function explainClue(
  puzzle: Puzzle,
  marks: Marks,
  clue: Clue,
  cell: Cell,
  mark: "tick" | "cross",
): string | null {
  const once = applyClueOnce(puzzle, marks, clue);
  if (!once) return null;
  const open = once.get(cell.rowCat, cell.colCat, cell.rowVal, cell.colVal);
  const settled =
    mark === "cross"
      ? !open
      : open && once.candidates(cell.rowCat, cell.colCat, cell.rowVal).length === 1;
  if (!settled) return null;

  switch (clue.kind) {
    case "not":
    case "same":
    case "either":
      return plainWhy(puzzle, marks, clue, cell, mark);
    case "immediate":
    case "before":
    case "distance":
    case "adjacent":
    case "between":
      return positionalWhy(puzzle, marks, clue, cell, mark);
    default:
      return null;
  }
}
